import type {
  CoreColumnChange,
  CustomFieldColumns,
  LocationValue,
  RateWrite
} from 'v5/core/entity-values/values.types';
import type {
  ContactRateBodyRow,
  ContactScalarColumns,
  CustomFieldBodyRow,
  LocationBodyRow
} from './contactBody.types';

/**
 * Helpers shared by `buildUpdateBody` and `buildCreateBody`. Internal to
 * `resource-write` — not part of the public contract.
 */

/** Child ids on the contact graph are `integer` columns; anything else is a local id that must never reach a payload. */
export function assertPersistedId(id: number, context: string): number {
  if (!Number.isInteger(id)) {
    throw new Error(
      `${context}: expected a persisted integer id, got ${String(id)}`
    );
  }
  return id;
}

/** `{ id }` when persisted, `{}` when `null` (insert). */
export function optionalIdProps(
  id: number | null,
  context: string
): { id?: number } {
  return id === null ? {} : { id: assertPersistedId(id, context) };
}

function assertFinite(value: number, context: string): number {
  if (!Number.isFinite(value)) {
    throw new Error(
      `${context}: expected a finite number, got ${String(value)}`
    );
  }
  return value;
}

function toFiniteOrNull(value: number | null, context: string): number | null {
  return value === null ? null : assertFinite(value, context);
}

/** Integer columns (fee percents): `Math.round` defensively. */
export function toIntegerColumn(
  value: number | null,
  context: string
): number | null {
  return value === null ? null : Math.round(assertFinite(value, context));
}

export function applyCoreChanges(
  target: ContactScalarColumns,
  changes: CoreColumnChange[]
): ContactScalarColumns {
  for (const change of changes) {
    switch (change.column) {
      case 'first_name':
      case 'last_name':
      case 'nick_name':
      case 'role':
        target[change.column] = change.value;
        break;
      case 'pronouns_option_id':
      case 'organisation_id':
      case 'represented_by_id':
        target[change.column] = change.value;
        break;
    }
  }
  return target;
}

/** Copies exactly the writable value columns — nothing else leaks into the row. */
export function pickCustomFieldColumns(
  columns: CustomFieldColumns
): CustomFieldColumns {
  return {
    string_value: columns.string_value,
    integer_value: columns.integer_value,
    float_value: columns.float_value,
    boolean_value: columns.boolean_value,
    date_value: columns.date_value,
    option_id: columns.option_id,
    multi_select_ids:
      columns.multi_select_ids === null ? null : [...columns.multi_select_ids],
    raw_value: columns.raw_value,
    raw_unit: columns.raw_unit
  };
}

export function customFieldRowWithoutId(
  definitionId: number,
  workspaceId: number | null,
  columns: CustomFieldColumns
): Omit<CustomFieldBodyRow, 'id'> {
  return {
    custom_field_definition_id: definitionId,
    enterprise_workspace_id: workspaceId,
    archived: false,
    ...pickCustomFieldColumns(columns)
  };
}

/** Value columns of a `contact_rates` row: everything but `id`, `contact_id` and `order`. */
export type ContactRateValueColumns = Omit<
  ContactRateBodyRow,
  'id' | 'contact_id' | 'order'
>;

/**
 * A rate-card row (`rateId` set) sends `rate_id` and `null` for every own
 * column — the card is the source of truth (see `ContactRateBodyRow`). A
 * custom rate sends its own integer columns.
 */
export function rateValueColumns(
  item: RateWrite,
  context: string
): ContactRateValueColumns {
  if (item.rateId !== null) {
    return {
      rate_id: assertPersistedId(item.rateId, `${context}.rate_id`),
      name: null,
      description: null,
      currency: null,
      amount: null,
      interval: null,
      agency_fee: null,
      payroll_fee: null,
      processing_fee: null
    };
  }
  return {
    rate_id: null,
    name: item.name,
    description: item.description,
    currency: item.currency,
    amount: toFiniteOrNull(item.amount, `${context}.amount`),
    interval: item.interval,
    agency_fee: toIntegerColumn(item.agencyFee, `${context}.agency_fee`),
    payroll_fee: toIntegerColumn(item.payrollFee, `${context}.payroll_fee`),
    processing_fee: toIntegerColumn(
      item.processingFee,
      `${context}.processing_fee`
    )
  };
}

/** New `locations` row: name and coordinates, as legacy `LocationInput` sent. */
export function locationRow(location: LocationValue): LocationBodyRow {
  return {
    name: location.name,
    coordinates:
      location.coordinates === null
        ? null
        : { lat: location.coordinates.lat, lon: location.coordinates.lon }
  };
}
